import React, { useEffect, useRef, useState } from "react"
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material"
import api from "../../../../base/services/ResApi"
import CloseIcon from "@mui/icons-material/Close"
import SaveIcon from "@mui/icons-material/Save"
import { assignmentOrganization, buildTerminalAssignment, hasGroup } from "./terminalAssignment"

// Used by both the row action and the bulk selection action.
const TerminalAssignmentModal = ({ terminals, onClose, onSaved }) => {
  const [mode, setMode] = useState("assign")
  const [destination, setDestination] = useState("")
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState("")
  const [retry, setRetry] = useState(0)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  const inFlight = useRef(false)
  let orgCode = ""
  let selectionError = ""
  try { orgCode = assignmentOrganization(terminals) } catch (err) { selectionError = err.message }

  useEffect(() => {
    if (!orgCode) return
    let active = true
    setLoading(true)
    setLoadError("")
    api.get("/groups", { headers: {
      Action: "V_ALL_APPR_GRP", Filter: "F_ORG_CODE", Value: orgCode,
      "Is-Pagination": "false", "Sort-Column": "grp_id", "Sort-Order": "asc",
    } }).then((response) => {
      if (active) setGroups((response.data?.dataList || []).filter((group) =>
        !group.orgCode || group.orgCode === orgCode))
    }).catch(() => {
      if (active) setLoadError("Unable to load groups. Please retry.")
    }).finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [orgCode, retry])

  let payload
  let validation = selectionError
  if (!validation) {
    try { payload = buildTerminalAssignment(terminals, mode, destination, groups) }
    catch (err) { validation = err.message }
  }
  const target = groups.find((group) => String(group.groupId) === String(destination))
  const save = async () => {
    if (inFlight.current || !payload) return
    inFlight.current = true
    setSaving(true)
    setError("")
    try {
      const response = await api.put("/terminals/assigns", payload)
      if (!response || Number(response.data?.code) >= 400) throw new Error(response?.data?.description || "Assignment was not confirmed.")
      onSaved(response.data?.description || "Terminal group assignments updated.")
      onClose()
    } catch (err) {
      setError(err.response?.data?.description || err.message || "Unable to update assignments. Please try again.")
    } finally {
      inFlight.current = false
      setSaving(false)
    }
  }

  return <Dialog open fullWidth maxWidth="md" onClose={() => { if (!saving) onClose() }} aria-labelledby="assignment-title"
    PaperProps={{ sx: { borderRadius: "10px" } }}>
    <DialogTitle id="assignment-title" sx={{ backgroundColor: "#116c6f", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
      Manage terminal groups
      <IconButton aria-label="Close assignment dialog" disabled={saving} onClick={onClose} sx={{ color: "inherit" }}>
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent dividers>
      <Stack spacing={2}>
        <Typography>{terminals.length} terminal{terminals.length === 1 ? "" : "s"} selected · Organization: {orgCode || "Multiple or missing organizations"}</Typography>
        <Typography variant="body2" color="text.secondary">Assign terminals to a group or remove their current group assignment. Their organization stays the same.</Typography>
        {selectionError && <Alert severity="error">{selectionError}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
        <TextField select label="Action" value={mode} disabled={saving || !!selectionError} onChange={(event) => { setMode(event.target.value); setError("") }}>
          <MenuItem value="assign">Assign / move to group</MenuItem>
          <MenuItem value="unassign">Unassign from current group</MenuItem>
        </TextField>
        {mode === "assign" && <>
          {loadError && <Alert severity="error" action={<Button disabled={saving} onClick={() => setRetry((value) => value + 1)}>Retry</Button>}>{loadError}</Alert>}
          <TextField select required label="Destination group" value={destination}
            disabled={loading || saving || !!selectionError || !!loadError}
            helperText={loading ? "Loading groups…" : "Only groups in the selected organization are available."}
            onChange={(event) => { setDestination(event.target.value); setError("") }}>
            {groups.map((group) => <MenuItem key={group.groupId} value={group.groupId}>{group.groupName || `Group ${group.groupId}`}</MenuItem>)}
          </TextField>
          {!loading && !loadError && !selectionError && !groups.length && <Alert severity="info">No approved groups are available in this organization.</Alert>}
        </>}
        <TableContainer sx={{ maxHeight: 300 }}>
          <Table size="small" stickyHeader aria-label="Assignment preview">
            <TableHead><TableRow><TableCell>Terminal serial</TableCell><TableCell>Current group</TableCell><TableCell>After saving</TableCell></TableRow></TableHead>
            <TableBody>{terminals.map((terminal, index) => <TableRow key={`${terminal.serialNo}-${index}`}>
              <TableCell sx={{ overflowWrap: "anywhere" }}>{terminal.serialNo}</TableCell>
              <TableCell>{hasGroup(terminal) ? terminal.groupName || `Group ${terminal.groupId}` : "Unassigned"}</TableCell>
              <TableCell>{mode === "unassign" ? "Unassigned" : target?.groupName || (target ? `Group ${target.groupId}` : "Choose a group")}</TableCell>
            </TableRow>)}</TableBody>
          </Table>
        </TableContainer>
        {validation && !selectionError && <Typography variant="body2" color="text.secondary">{validation}</Typography>}
        {payload && <Alert severity="info">Review the changes above, then save. Terminals already in the requested state will be skipped.</Alert>}
      </Stack>
    </DialogContent>
    <DialogActions sx={{ p: "1.25rem" }}>
      <Button variant="outlined" color="error" disabled={saving} onClick={onClose}>Cancel</Button>
      <Button variant="contained" color="success" startIcon={<SaveIcon />} disabled={saving || !payload || (mode === "assign" && (loading || !!loadError))} onClick={save}>
        {saving ? "Saving…" : "Save assignments"}
      </Button>
    </DialogActions>
  </Dialog>
}
export default TerminalAssignmentModal
